// ThemePicker — Cut AM.
//
// Three palettes ship: parchment (default — Sand & Inkwell), midnight,
// ocean. Selection persists in localStorage. The data-palette attribute
// is set by index.html before React mounts, so there's no flash.

import { useEffect, useState } from 'react';
import clsx from 'clsx';

export type Palette = 'parchment' | 'midnight' | 'ocean';

const STORAGE_KEY = 'raftel_palette';
const DEFAULT_PALETTE: Palette = 'parchment';

const PALETTES: Array<{ id: Palette; label: string; swatch: string }> = [
  { id: 'parchment', label: 'Sand', swatch: '#F2EBDC' },
  { id: 'midnight',  label: 'Midnight', swatch: '#0b1020' },
  { id: 'ocean',     label: 'Ocean', swatch: '#07151e' },
];

export function getStoredPalette(): Palette {
  try {
    const v = localStorage.getItem(STORAGE_KEY) as Palette | null;
    if (v && PALETTES.some((p) => p.id === v)) return v;
  } catch { /* ignore */ }
  return DEFAULT_PALETTE;
}

export function applyPalette(palette: Palette): void {
  document.documentElement.setAttribute('data-palette', palette);
  try { localStorage.setItem(STORAGE_KEY, palette); } catch { /* ignore */ }
}

function ThemePicker({ compact = false }: { compact?: boolean }) {
  const [active, setActive] = useState<Palette>(getStoredPalette);

  useEffect(() => { applyPalette(active); }, [active]);

  if (compact) {
    return (
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] text-muted px-1">
        <span>Theme</span>
        <div className="flex gap-1">
          {PALETTES.map((p) => (
            <button
              key={p.id}
              onClick={() => setActive(p.id)}
              title={p.label}
              className={clsx(
                'w-4 h-4 rounded-full border transition-all',
                active === p.id ? 'border-accent ring-1 ring-accent/40 ring-offset-1' : 'border-line hover:border-muted',
              )}
              style={{ background: p.swatch }}
              aria-label={`Switch to ${p.label} theme`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      {PALETTES.map((p) => (
        <button
          key={p.id}
          onClick={() => setActive(p.id)}
          className={clsx(
            'flex items-center gap-2 px-3 py-1.5 rounded border text-xs transition-colors',
            active === p.id ? 'border-accent text-accent bg-accent-soft' : 'border-line text-muted hover:text-text',
          )}
        >
          <span className="w-3 h-3 rounded-full border border-line" style={{ background: p.swatch }} />
          {p.label}
        </button>
      ))}
    </div>
  );
}

export default ThemePicker;
